// ─── DATE FORMAT MIGRATION SWEEP ─────────────────────────────────────────────
// One-shot data-hygiene pass that normalises DD/MM/YYYY date strings to ISO
// YYYY-MM-DD inside document changeHistory[] and reviewHistory[], gated by
// `profile.dateFormatMigrationDone` on profile.json. Idempotent.
//
// A confirmed defect stored a changeHistory entry date as European DD/MM/YYYY
// (e.g. '30/01/2023') instead of the canonical ISO YYYY-MM-DD. The previous
// commit hardened computeNextReviewDate / addMonths so this can no longer crash
// the Document form; this sweep cleans up the malformed data itself.
//
// Scope is deliberately narrow: only the format DD/MM/YYYY with a four-digit
// year, only the two named date fields, only the documents collection. The
// migration NEVER recomputes version or nextReviewDate — both are derived fields
// the user can override manually, and recomputing them would silently destroy
// legitimate overrides.

import {
  readOrgFile,
  writeOrgFile,
  listOrgCollection,
} from './FileSystemAdapter';

const todayIso = () => new Date().toISOString().slice(0, 10);

// Push a changeLog entry on `record` describing one date normalisation.
function pushNormalizeLog(record, field, previousValue, newValue) {
  const log = Array.isArray(record.changeLog) ? [...record.changeLog] : [];
  log.push({
    timestamp: new Date().toISOString(),
    field,
    previousValue,
    newValue,
    changedBy: 'system',
  });
  return log;
}

/**
 * Pure normaliser. Returns an ISO 'YYYY-MM-DD' string only when `value` is
 * exactly a DD/MM/YYYY string (two digits, slash, two digits, slash, four
 * digits) AND the components form a real calendar date — interpreting the FIRST
 * group as the day and the SECOND group as the month (European format).
 *
 * In every other case (already ISO, ISO datetime, empty, null, non-string, or a
 * pattern match that is an impossible date such as day 32 or month 13) it
 * returns null, signalling "leave the original value untouched".
 *
 * Never throws on any input.
 */
export function normalizeDdmmyyyyToIso(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  // Round-trip through a UTC date to reject impossible days (e.g. 31/04, 29/02
  // in a non-leap year): JS rolls such dates over, so the components won't match.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return null;
  }
  // The captured groups are guaranteed two/four digits — emit them directly as
  // YYYY-MM-DD (year-month-day) with the day and month swapped into ISO order.
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// Update a single record in the `documents/` collection. `mutate` returns `true`
// if it actually changed something (so the file is rewritten), `false` otherwise.
async function processItemCollection(orgId, folder, mutate) {
  let items = [];
  try {
    items = await listOrgCollection(orgId, folder);
  } catch {
    return;
  }
  for (const item of items) {
    if (!item || !item.id) continue;
    const dirty = mutate(item);
    if (dirty) {
      item.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, `${folder}/${item.id}.json`, item);
    }
  }
}

// ── PUBLIC API ────────────────────────────────────────────────────────────────
export async function runDateFormatMigrationIfNeeded(orgId) {
  let profile;
  try {
    profile = await readOrgFile(orgId, 'profile.json');
  } catch {
    return;
  }
  if (!profile || profile.dateFormatMigrationDone === true) return;

  // ── documents/<id>.json ────────────────────────────────────────────────────
  // Walk changeHistory[].date and reviewHistory[].date; convert only the
  // DD/MM/YYYY entries. version and nextReviewDate are intentionally never read
  // or rewritten — they are manual overrides (GRC_ARCHITECTURE.md §4).
  await processItemCollection(orgId, 'documents', (doc) => {
    let dirty = false;
    if (Array.isArray(doc.changeHistory)) {
      doc.changeHistory.forEach((entry, idx) => {
        if (!entry) return;
        const iso = normalizeDdmmyyyyToIso(entry.date);
        if (iso !== null) {
          doc.changeLog = pushNormalizeLog(doc, `changeHistory[${idx}].date`, entry.date, iso);
          entry.date = iso;
          dirty = true;
        }
      });
    }
    if (Array.isArray(doc.reviewHistory)) {
      doc.reviewHistory.forEach((entry, idx) => {
        if (!entry) return;
        const iso = normalizeDdmmyyyyToIso(entry.date);
        if (iso !== null) {
          doc.changeLog = pushNormalizeLog(doc, `reviewHistory[${idx}].date`, entry.date, iso);
          entry.date = iso;
          dirty = true;
        }
      });
    }
    return dirty;
  });

  // ── profile.json — flag + date ─────────────────────────────────────────────
  // Set even when no document needed changes, so the sweep never re-scans.
  profile.dateFormatMigrationDone = true;
  profile.dateFormatMigrationDate = todayIso();
  profile.updatedAt = new Date().toISOString();
  await writeOrgFile(orgId, 'profile.json', profile);
}
